import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFavoritesPanel } from './FavoritesPanel';
import { useCanFavorite } from '@/hooks/useCanFavorite';

/**
 * The Hub's one app-bar item is the Favorites ★ (Jesse, 2026-10-07); the
 * left nav belongs to the project and to you.
 *
 * - Messages is the Hub chat applet (apps/hub/api/applets/chat), which the app
 *   bar loads on EVERY Solid app, the Hub included. Direct messages have no
 *   screen in the Hub; the applet's popup is the whole experience.
 * - Activity, Support and Settings are at the bottom of the left nav
 *   (ProjectNav), not here.
 */
export function useAppBarItems() {
  const toggleFavorites = useFavoritesPanel((s) => s.toggle);
  const canFavorite = useCanFavorite();

  useEffect(() => {
    const ext = window.SolidHub?.extensions;
    // Favorites are for agency people (useCanFavorite)
    if (!ext || !canFavorite) return;
    const offFavorites = ext.registerBottomNav({
      id: 'favorites',
      label: 'Favorites',
      icon: 'Star',
      order: 60,
      onClick: toggleFavorites,
      tooltip: 'Favorite projects',
    });
    return () => offFavorites();
  }, [toggleFavorites, canFavorite]);
}

/**
 * Without an app bar (standalone dev) the ★ would have no way in, so it sits
 * in the topbar instead. There's no chat there: it only exists as the applet.
 * Renders nothing when the app bar is there.
 */
export function AppBarFallback() {
  const [hasAppBar, setHasAppBar] = useState(() => !!window.SolidHub?.extensions);
  const canFavorite = useCanFavorite();

  // The rail script can finish loading after the first render
  useEffect(() => {
    if (hasAppBar) return;
    const t = setInterval(() => window.SolidHub?.extensions && setHasAppBar(true), 500);
    const stop = setTimeout(() => clearInterval(t), 15_000);
    return () => {
      clearInterval(t);
      clearTimeout(stop);
    };
  }, [hasAppBar]);

  if (hasAppBar || !canFavorite) return null;
  return <FavoritesTopbarButton />;
}

/** The ★ for standalone dev; FavoritesPanel anchors to `[data-favorites-anchor]`. */
function FavoritesTopbarButton() {
  const { open, toggle } = useFavoritesPanel();
  return (
    <button
      type="button"
      data-favorites-anchor
      onClick={toggle}
      aria-expanded={open}
      aria-label="Favorite projects"
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-grey-100',
        open ? 'text-warning' : 'text-grey-500',
      )}
    >
      <Star className={cn('h-5 w-5', open && 'fill-warning')} aria-hidden />
    </button>
  );
}
